import { FC } from "react";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import {
  DashboardIdentity,
  getDashboardIdentityLabel,
} from "../lib/dashboardIdentity";
import { IDENTITY_ICON, IDENTITY_STYLE } from "../lib/dashboardIdentityStyle";

const UNPUBLISHED_CHANGES_LABEL = "Ikke-publicerede ændringer";

interface DashboardIdentityPillProps {
  identity: DashboardIdentity;
  /** `row`: the compact form on a dropdown row, under a group heading that names the audience. */
  size?: "header" | "row";
}

/**
 * Says in words whose dashboard this is (LAR-661); the icon only supports the text, since
 * telling the person and building icons apart was what failed in LAR-280.
 */
export const DashboardIdentityPill: FC<DashboardIdentityPillProps> = ({
  identity,
  size = "header",
}) => {
  const label = getDashboardIdentityLabel(identity);
  const Icon = IDENTITY_ICON[identity.kind];
  const isHeader = size === "header";

  return (
    <Badge
      variant="outline"
      className={cn(
        "rounded-full font-semibold",
        isHeader ? "h-6 gap-1.5 px-2.5" : "h-5 px-2 text-[11px]",
        IDENTITY_STYLE[identity.kind]
      )}
    >
      {isHeader && <Icon aria-hidden="true" />}
      {isHeader ? label.long : label.short}
    </Badge>
  );
};

interface UnpublishedChangesChipProps {
  /** `dot`: only the marker, for a dropdown row; the words move to its accessible name. */
  size?: "header" | "dot";
}

/** A published dashboard edited since: colleagues still see the published version. */
export const UnpublishedChangesChip: FC<UnpublishedChangesChipProps> = ({
  size = "header",
}) => {
  if (size === "dot") {
    return (
      <span
        role="img"
        aria-label={UNPUBLISHED_CHANGES_LABEL}
        title={UNPUBLISHED_CHANGES_LABEL}
        className="inline-block size-2 shrink-0 rounded-full bg-warning"
      />
    );
  }

  return (
    <Badge
      variant="outline"
      className={cn(
        "h-6 gap-1.5 rounded-full px-2.5 font-semibold",
        IDENTITY_STYLE["organisation-draft"]
      )}
    >
      <span
        aria-hidden="true"
        className="inline-block size-1.5 rounded-full bg-warning"
      />
      {UNPUBLISHED_CHANGES_LABEL}
    </Badge>
  );
};
